import React, { createContext, useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const WaitlistModalContext = createContext(null);

export const useWaitlistModal = () => {
  const ctx = useContext(WaitlistModalContext);
  if (!ctx) throw new Error('useWaitlistModal must be used within WaitlistModalProvider');
  return ctx;
};

// --- Automated Welcome Email Sender (EmailJS) ---
const sendWelcomeEmail = async (userEmail, userName) => {
  const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      service_id: 'service_c97oapr',
      template_id: 'template_8m03v99',
      user_id: 'rq57rUzbSPnJX2ovd',
      template_params: {
        to_email: userEmail,
        to_name: userName || 'there',
      }
    })
  });

  const responseText = await response.text();
  console.log('EmailJS response', {
    status: response.status,
    ok: response.ok,
    body: responseText
  });

  if (!response.ok) {
    throw new Error(`EmailJS failed (${response.status}): ${responseText || 'No response body'}`);
  }
};

export const WaitlistModalProvider = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalEmail, setModalEmail] = useState('');
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const [isModalSubmitted, setIsModalSubmitted] = useState(false);
  const [modalSubmitError, setModalSubmitError] = useState('');

  const openWaitlistModal = () => setIsModalOpen(true);

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    setIsModalSubmitting(true);
    setModalSubmitError('');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: '36bd61b8-e929-4fd7-ae6f-049dd98ecde2',
          subject: 'New Form Submission from SaniQ',
          from_name: 'SaniQ Website',
          name: modalEmail.split('@')[0] || 'Waitlist',
          email: modalEmail,
          replyto: modalEmail,
          message: `New waitlist signup: ${modalEmail}`,
          'Email Address': modalEmail,
        })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Web3Forms submission failed');
      }

      await sendWelcomeEmail(modalEmail, modalEmail.split('@')[0] || undefined);

      setIsModalSubmitted(true);

      setTimeout(() => {
        setIsModalOpen(false);
        setTimeout(() => {
          setIsModalSubmitted(false);
          setModalEmail('');
        }, 500);
      }, 2500);

    } catch (error) {
      console.error('Modal submission failed', error);
      setModalSubmitError(`Submission failed: ${error.message || 'Please check details and try again.'}`);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  return (
    <WaitlistModalContext.Provider value={{ openWaitlistModal }}>
      {children}

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => !isModalSubmitting && setIsModalOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg glass bg-[#0a0a0f] rounded-3xl p-8 md:p-10 shadow-2xl overflow-hidden border border-white/10"
            >
              {!isModalSubmitting && !isModalSubmitted && (
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors p-2"
                >
                  <SafeIcon name="X" className="text-2xl" />
                </button>
              )}

              {isModalSubmitted ? (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center py-10 gap-4"
                >
                  <SafeIcon name="CheckCircle" className="text-cyan-400 text-6xl mb-2" />
                  <h3 className="text-white text-2xl font-light">You're in line for launch.</h3>
                  <p className="text-white/40 text-sm text-center">Thank you for registering for launch updates and founding-member benefits.</p>
                </motion.div>
              ) : (
                <>
                  <div className="mb-8">
                    <h3 className="text-3xl font-light text-white mb-3">Register for Launch Updates</h3>
                    <p className="text-white/40 text-sm leading-relaxed">Join the waitlist for the world's first intelligent disinfection mat.</p>
                  </div>

                  <form onSubmit={handleModalSubmit} className="flex flex-col gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="px-1 text-[10px] uppercase tracking-widest text-white/50">Email</label>
                      <input
                        required
                        type="email"
                        value={modalEmail}
                        onChange={(e) => setModalEmail(e.target.value)}
                        disabled={isModalSubmitting}
                        className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white transition-colors focus:border-cyan-500 focus:outline-none disabled:opacity-50"
                        placeholder="you@example.com"
                        autoComplete="email"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isModalSubmitting}
                      className="w-full py-4 bg-cyan-500 text-black font-bold rounded-xl text-sm uppercase tracking-widest hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center mt-2"
                    >
                      {isModalSubmitting ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full"
                        />
                      ) : (
                        'Register Now'
                      )}
                    </button>
                    <p className="text-white/35 text-[11px] leading-relaxed text-center">
                      We respect your privacy. We will only send important SaniQ launch updates and never spam or share your details.
                    </p>
                    <p className="text-white/35 text-[11px] leading-relaxed text-center">
                      By registering, you agree to our <a href="/privacy" className="text-cyan-400 hover:text-cyan-300 transition-colors">Privacy Policy</a>.
                    </p>
                    {modalSubmitError && (
                      <p className="text-red-400 text-xs">{modalSubmitError}</p>
                    )}
                  </form>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </WaitlistModalContext.Provider>
  );
};
